import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  CircularProgress,
  InputLabel,
  Checkbox,
  FormGroup,
  Chip,
} from "@mui/material";
import {
  NavigateNext as NavigateNextIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../../../contexts/ProfileContext";
import Cookies from "js-cookie";
import axiosInstance from "../../../axios/axiosInstance";
import { Tooltip, Stack, IconButton } from "@mui/material";
import { Delete } from "@mui/icons-material";
import { jwtDecode } from "jwt-decode";

const PersonalInfo = () => {
  const navigate = useNavigate();
  const {
    profile,
    loading,
    updateTempPersonalInfo,
    countries: contextCountries,
    tempProfileData,
    isProfileReadOnly,
    isEditingProfile,
    setIsEditingProfile,
    hasProfileChanges,
    setHasProfileChanges,
    hasExistingProfile,
    showFullProfileFields,
  } = useProfile();

  const [jobFunctions, setJobFunctions] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState("");
  const [localCountries, setLocalCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [citySearch, setCitySearch] = useState("");
  const [countrySearch, setCountrySearch] = useState("");
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const workEligibleSwissOptions = [
    { value: "swiss_citizen", label: "Swiss citizen" },
    {
      value: "valid_swiss_permit",
      label: "Currently hold a valid Swiss work permit (B, C, L, or G)",
    },
    {
      value: "eu_efta_citizen",
      label: "EU / EFTA citizen (eligible to work in Switzerland)",
    },
    {
      value: "other_nationality",
      label: "Other nationality (work permit required)",
    },
  ];

  const handleNoticeChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      noticePeriod: { ...prev.noticePeriod, [field]: value },
    }));
  };

  const token = Cookies.get("token");
  const userEmail = token ? jwtDecode(token).email : null;

  // Use local countries if context countries are not available
  const countries =
    contextCountries && contextCountries.length > 0
      ? contextCountries
      : localCountries;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: userEmail,
    isAge18OrAbove: false,
    country: "",
    city: "",
    workEligibleSwiss: [],
    workEligibleCountries: ["CH"],
    jobSearchStatus: "passive",
    function: "",
    noticePeriod: {
      value: 7,
      unit: "days",
      immediate: false,
    },
    // languages: [],
    preferredCompanyTypes: [],
    preferredWorkEnvironments: [],
    // softSkills: [],
  });

  // Helper function to find function ID by name
  const findFunctionIdByName = (functionName) => {
    if (!functionName || !jobFunctions.length) return "";
    const found = jobFunctions.find((f) => f.name === functionName);
    return found ? found.id : "";
  };

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Track local edits on this page to prevent useEffect from overwriting them
  const hasLocalEdits = useRef(false);

  useEffect(() => {
    // Don't overwrite local form state while user has made edits on this page
    if (isEditingProfile && hasLocalEdits.current) return;

    // If read-only, always use backend profile data
    let dataSource = isProfileReadOnly
      ? profile
      : tempProfileData.personalInfo || profile;

    // const getData = localStorage.getItem("tempProfileData");

    // // Parse existing data or set empty object if none
    // let profileDataLocal = getData ? JSON.parse(getData) : {};

    // // Check if PersonalInfo key is null or missing
    // if (!profileDataLocal.personalInfo) {
    //   // Add default PersonalInfo data
    //   profileDataLocal.personalInfo = dataSource;
    //   // Save back to localStorage with the rest of the data intact
    //   localStorage.setItem("tempProfileData", JSON.stringify(profileDataLocal));

    //   if (typeof updateTempPersonalInfo === "function" && dataSource) {
    //     updateTempPersonalInfo(dataSource);
    //   }
    // }
    // Fallback: if no data source found, try to load directly from localStorage
    if (!dataSource) {
      try {
        const saved = localStorage.getItem("tempProfileData");
        if (saved) {
          const parsed = JSON.parse(saved);
          dataSource = parsed.personalInfo;
        }
      } catch (error) {
        console.error(
          "🔍 DEBUG: Error loading from localStorage fallback:",
          error,
        );
      }
    }

    if (dataSource) {
      const newFormData = {
        firstName: dataSource.first_name || dataSource.firstName || "",
        lastName: dataSource.last_name || dataSource.lastName || "",
        email: dataSource.email || userEmail || "",
        isAge18OrAbove: Boolean(
          dataSource.is_age_18_or_above || dataSource.isAge18OrAbove || false,
        ),
        country: dataSource.country_code || dataSource.country || "",
        // city: dataSource.city || "",
        workEligibleCountries:
          (dataSource.workEligibleCountries || []).includes("CH") ||
          (dataSource.workEligibleCountries || []).length === 0
            ? ["CH"]
            : ["CH"], // Always ensure Switzerland is the only one
        workEligibleSwiss: dataSource.workEligibleSwiss || [],
        jobSearchStatus:
          dataSource.job_search_status ||
          dataSource.jobSearchStatus ||
          "passive",
        function: dataSource.current_industry
          ? findFunctionIdByName(dataSource.current_industry)
          : dataSource.function || "",
        preferredCompanyTypes: dataSource.preferredCompanyTypes || [],
        preferredWorkEnvironments: dataSource.preferredWorkEnvironments || [],
        noticePeriod: dataSource.noticePeriod || {
          value: 7,
          unit: "days",
          immediate: false,
        },
        // languages: dataSource.languages || [],
        // softSkills: dataSource.softSkills || [],
      };

      setFormData(newFormData);
    } else if (tempProfileData.personalInfo === null) {
      // Only reset form if we don't have any data AND tempProfileData.personalInfo is explicitly null
      // This prevents resetting when tempProfileData is still loading
      console.log("🔍 DEBUG: No data source found, resetting form");
      setFormData({
        firstName: "",
        lastName: "",
        email: userEmail,
        isAge18OrAbove: false,
        country: "",
        // city: "",
        workEligibleSwiss: [],
        workEligibleCountries: ["CH"],
        jobSearchStatus: "passive",
        function: "",
        noticePeriod: {
          value: 7,
          unit: "days",
          immediate: false,
        },
        // languages: [],
        preferredCompanyTypes: [],
        preferredWorkEnvironments: [],
        // softSkills: [],
      });
    }
  }, [profile, tempProfileData.personalInfo, isProfileReadOnly, jobFunctions]);

  // Debounced fetch for countries
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (countrySearch.trim().length > 0) {
        fetchCountriesSearch(countrySearch);
      } else {
        setLocalCountries(countries);
      }
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [countrySearch]);

  // Debounced fetch for cities
  // useEffect(() => {
  //   if (!formData.country) return;

  //   const delayDebounce = setTimeout(() => {
  //     if (citySearch.trim().length > 0) {
  //       fetchCities(formData.country, citySearch);
  //     } else {
  //       setCities([]);
  //     }
  //   }, 400);

  //   return () => clearTimeout(delayDebounce);
  // }, [citySearch, formData.country]);

  const fetchCountriesSearch = async (search) => {
    try {
      setLoadingCountries(true);
      const res = await axiosInstance.get(
        `/api/profile/countries?search=${search}`,
      );
      setLocalCountries(res.data || []);
    } catch (err) {
      console.error("Failed to fetch countries", err);
    } finally {
      setLoadingCountries(false);
    }
  };

  const fetchCities = async (countryCode, name) => {
    try {
      setLoadingCities(true);
      const res = await axiosInstance.get(
        `/api/profile/cities?country_code=${countryCode}&name=${name}`,
      );
      setCities(res.data || []);
    } catch (err) {
      console.error("Failed to fetch cities", err);
    } finally {
      setLoadingCities(false);
    }
  };

  // Fetch job functions on component mount
  useEffect(() => {
    fetchJobFunctions();
  }, []);

  // Fetch countries if not available from context
  useEffect(() => {
    if (
      (!contextCountries || contextCountries.length === 0) &&
      localCountries.length === 0
    ) {
      fetchCountries();
    }
  }, [contextCountries, localCountries]);

  const fetchCountries = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/profile/countries`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) throw new Error("Failed to fetch countries");
      const data = await response.json();
      setLocalCountries(data);
    } catch (error) {
      console.error("Failed to load countries:", error);
    }
  };

  // Fetch job roles when function changes
  useEffect(() => {
    if (selectedFunction) {
      fetchJobRoles(selectedFunction);
    } else {
      setJobRoles([]);
      setFormData((prev) => ({ ...prev, currentRole: "" }));
    }
  }, [selectedFunction]);

  const fetchJobFunctions = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/profile/job-functions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) throw new Error("Failed to fetch job functions");
      const data = await response.json();
      setJobFunctions(data);
    } catch (error) {
      setError("Failed to load job functions");
    }
  };

  const fetchJobRoles = async (functionId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/profile/job-roles/${functionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) throw new Error("Failed to fetch job roles");
      const data = await response.json();
      setJobRoles(data);
    } catch (error) {
      setError("Failed to load job roles");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "currentIndustry") {
      setSelectedFunction(value);
    }
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
      return newData;
    });

    // Mark as changed when editing
    if (isEditingProfile) {
      console.log("Marking profile as changed");
      hasLocalEdits.current = true;
      setHasProfileChanges(true);
    }
  };

  const companyTypes = [
    { label: "Corporate", value: "corporate" },
    { label: "Start-up/Scale-up", value: "start-up/scale-up" },
    {
      label: "Entrepreneurial/Innovative",
      value: "entrepreneurial/innovative",
    },
    { label: "Mid-size", value: "mid-size" },
    { label: "Family-owned", value: "family-owned" },

    { label: "Other", value: "other" },
  ];

  const workEnvironments = [
    {
      label: "Fast-paced, goal-oriented and performance driven",
      value: "fast-paced, goal-oriented and performance driven",
    },
    {
      label: "Creative, energetic and socially engaging",
      value: "creative, energetic and socially engaging",
    },
    {
      label: "Calm, predictable, and cooperative",
      value: "calm, predictable, and cooperative",
    },
    {
      label: "Structured, organized and rule-based",
      value: "structured, organized and rule-based",
    },
  ];

  useEffect(() => {
    if (formData.country) {
      fetchCities(formData.country, ""); // fetch all cities for the selected country
    } else {
      setCities([]); // clear when no country
    }
  }, [formData.country]);

  const handleContinue = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.firstName || formData.firstName.trim() == "") {
      setError("First name is required");
      return;
    }

    if (!formData.lastName || formData.lastName.trim() == "") {
      setError("Last name is required");
      return;
    }

    if (!formData.noticePeriod.value && formData.noticePeriod.value !== 0) {
      setError("Notice period value is required");
      return;
    }
    if (formData.noticePeriod.value && formData.noticePeriod.value < 0) {
      setError("Notice period value cannot be negative");
      return;
    }

    if (formData.noticePeriod.value && formData.noticePeriod.value > 100) {
      setError("Notice period value cannot be more than 100");
      return;
    }

    if (!formData.workEligibleSwiss.length < 0) {
      setError("Please select your work eligibility for switzerland option");
      return;
    }

    // if (!formData.country) {
    //   setError("Nationality is required");
    //   return;
    // }

    // if (!formData.email || formData.email.trim() == "") {
    //   setError("Email is required");
    //   return;
    // }
    // Validate age requirement
    if (!formData.isAge18OrAbove) {
      setError("You must be 18 years or above to use this platform");
      return;
    }

    try {
      const dataToSubmit = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        isAge18OrAbove: formData.isAge18OrAbove,
        country: formData.country,
        city: formData.city,
        workEligibleCountries: formData.workEligibleCountries,
        jobSearchStatus: formData.jobSearchStatus,
        function: formData.function,
        preferredCompanyTypes: formData.preferredCompanyTypes,
        preferredWorkEnvironments: formData.preferredWorkEnvironments,
        noticePeriod: formData.noticePeriod,
        workEligibleSwiss: formData.workEligibleSwiss,
      };

      // Save to temporary storage instead of database
      await updateTempPersonalInfo(dataToSubmit);
      setSuccess("Personal information saved temporarily");

      // Navigate to next step after a brief delay to show success message
      setTimeout(() => {
        navigate("/candidate/profile/career-details");
      }, 1000);
    } catch (error) {
      setError(error.message || "Failed to save personal information");
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 4, width: "100%", mx: "auto", mt: 0 }}>
      {/* Header with Edit Button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" gutterBottom>
            Personal Info
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Let recruiters get to know you better!
          </Typography>
        </Box>

        {/* Show Edit Profile button only for users with existing profiles */}
        {hasExistingProfile && !isProfileReadOnly && !isEditingProfile && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => setIsEditingProfile(true)}
            sx={{ minWidth: 160 }}
          >
            Edit Profile
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleContinue}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Name *
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="First Name *"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              disabled={isProfileReadOnly || !isEditingProfile}
              InputProps={{ readOnly: isProfileReadOnly || !isEditingProfile }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="Last Name *"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              disabled={isProfileReadOnly || !isEditingProfile}
              InputProps={{ readOnly: isProfileReadOnly || !isEditingProfile }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Email address *
            </Typography>
            <TextField
              fullWidth
              placeholder="Email Address"
              name="email"
              type="email"
              value={formData.email}
              disabled
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="isAge18OrAbove"
                  checked={formData.isAge18OrAbove}
                  onChange={handleChange}
                  color="primary"
                  disabled={isProfileReadOnly || !isEditingProfile}
                />
              }
              label="I am at least 18 years old. *"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Work eligibility for Switzerland *
            </Typography>

            <FormControl
              fullWidth
              disabled={isProfileReadOnly || !isEditingProfile}
            >
              <Select
                value={formData.workEligibleSwiss?.[0] || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (isEditingProfile) {
                    hasLocalEdits.current = true;
                    setHasProfileChanges(true);
                  }
                  setFormData((prev) => ({
                    ...prev,
                    workEligibleSwiss: value ? [value] : [],
                  }));
                }}
                displayEmpty
              >
                <MenuItem value="">
                  <em>Select Eligibility</em>
                </MenuItem>

                {workEligibleSwissOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Select countries that you are eligible to work
            </Typography>
            <Autocomplete
              multiple
              options={
                countries
                  ? countries.filter(
                      (c) => c.name === "Switzerland" || c.code === "CH",
                    )
                  : []
              }
              disabled={isProfileReadOnly || !isEditingProfile}
              getOptionLabel={(option) => option.name || ""}
              value={
                countries
                  ? countries.filter(
                      (country) =>
                        formData.workEligibleCountries.includes(country.code) &&
                        (country.code === "CH" ||
                          country.name === "Switzerland"),
                    )
                  : []
              }
              onChange={(event, newValue) => {
                if (isEditingProfile) {
                  hasLocalEdits.current = true;
                  setHasProfileChanges(true);
                }
                setFormData((prev) => ({
                  ...prev,
                  workEligibleCountries: newValue.map(
                    (country) => country.code,
                  ),
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    countries && countries.length > 0
                      ? "Add countries"
                      : "Loading countries..."
                  }
                  InputLabelProps={{ shrink: true }}
                  disabled={isProfileReadOnly || !isEditingProfile}
                />
              )}
              noOptionsText="No countries available"
              loading={!countries || countries.length === 0}
            />
          </Grid>

          {showFullProfileFields && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Select preferred company types
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose one or more
            </Typography>
            <Autocomplete
              multiple
              options={companyTypes}
              disabled={isProfileReadOnly || !isEditingProfile}
              getOptionLabel={(option) => option.label}
              value={
                formData.preferredCompanyTypes
                  ? companyTypes.filter((type) =>
                      formData.preferredCompanyTypes.includes(type.value),
                    )
                  : []
              }
              onChange={(event, newValue) => {
                if (isEditingProfile) {
                  hasLocalEdits.current = true;
                  setHasProfileChanges(true);
                }
                setFormData((prev) => ({
                  ...prev,
                  preferredCompanyTypes: newValue.map((type) => type.value),
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select preferred company types"
                  InputLabelProps={{ shrink: true }}
                  disabled={isProfileReadOnly || !isEditingProfile}
                />
              )}
              noOptionsText="No company types available"
            />
          </Grid>
          )}

          {showFullProfileFields && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Select preferred work environments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose one or more
            </Typography>
            <Autocomplete
              multiple
              options={workEnvironments}
              disabled={isProfileReadOnly || !isEditingProfile}
              getOptionLabel={(option) => option.label}
              value={
                formData.preferredWorkEnvironments
                  ? workEnvironments.filter((env) =>
                      formData.preferredWorkEnvironments.includes(env.value),
                    )
                  : []
              }
              onChange={(event, newValue) => {
                if (isEditingProfile) {
                  hasLocalEdits.current = true;
                  setHasProfileChanges(true);
                }
                setFormData((prev) => ({
                  ...prev,
                  preferredWorkEnvironments: newValue.map((env) => env.value),
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select preferred work environment"
                  InputLabelProps={{ shrink: true }}
                  disabled={isProfileReadOnly || !isEditingProfile}
                />
              )}
              noOptionsText="No work environments available"
            />
          </Grid>
          )}

          {/* Notice Period */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Notice Period</InputLabel>
              <Select
                label="Notice Period"
                value={
                  formData.noticePeriod?.value === 0 ? "immediate" : "custom"
                }
                disabled={isProfileReadOnly || !isEditingProfile}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  if (isEditingProfile) {
                    hasLocalEdits.current = true;
                    setHasProfileChanges(true);
                  }
                  setFormData((prev) => {
                    if (selectedValue === "immediate") {
                      return {
                        ...prev,
                        noticePeriod: { value: 0, unit: "days" },
                      };
                    } else {
                      // Keep previous value if valid, else set to empty string
                      const prevValue =
                        prev.noticePeriod?.value > 0
                          ? prev.noticePeriod.value
                          : 7;
                      return {
                        ...prev,
                        noticePeriod: {
                          value: prevValue,
                          unit: prev.noticePeriod?.unit || "days",
                        },
                      };
                    }
                  });
                }}
              >
                <MenuItem value="immediate">Available Immediately</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
            {/* Show inputs only if not immediate (value !== 0) */}
            {formData.noticePeriod?.value !== 0 && (
              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <TextField
                  label="Notice Period Value"
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  value={
                    formData.noticePeriod?.value === ""
                      ? ""
                      : formData.noticePeriod?.value || ""
                  }
                  disabled={isProfileReadOnly || !isEditingProfile}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (isEditingProfile) {
                      hasLocalEdits.current = true;
                      setHasProfileChanges(true);
                    }
                    // Allow empty string while typing
                    if (inputValue === "") {
                      setFormData((prev) => ({
                        ...prev,
                        noticePeriod: {
                          ...prev.noticePeriod,
                          value: "",
                        },
                      }));
                      return;
                    }

                    const value = Number(inputValue);

                    // Validate range: 0-100
                    if (value < 0 || value > 100) {
                      return; // Don't update if out of range
                    }

                    // If user enters 0, switch to immediate
                    if (value === 0) {
                      setFormData((prev) => ({
                        ...prev,
                        noticePeriod: {
                          ...prev.noticePeriod,
                          value: 0,
                        },
                      }));
                    } else {
                      // For any other number, just update the value
                      setFormData((prev) => ({
                        ...prev,
                        noticePeriod: {
                          ...prev.noticePeriod,
                          value: value,
                        },
                      }));
                    }
                  }}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    value={formData.noticePeriod?.unit || "days"}
                    label="Unit"
                    disabled={isProfileReadOnly || !isEditingProfile}
                    onChange={(e) => {
                      if (isEditingProfile) {
                        hasLocalEdits.current = true;
                        setHasProfileChanges(true);
                      }
                      setFormData((prev) => ({
                        ...prev,
                        noticePeriod: {
                          ...prev.noticePeriod,
                          unit: e.target.value,
                        },
                      }));
                    }}
                  >
                    <MenuItem value="days">Days</MenuItem>
                    <MenuItem value="weeks">Weeks</MenuItem>
                    <MenuItem value="months">Months</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Are you looking for a job currently?
            </Typography>
            <RadioGroup
              name="jobSearchStatus"
              value={formData.jobSearchStatus}
              onChange={handleChange}
              disabled={isProfileReadOnly || !isEditingProfile}
            >
              <FormControlLabel
                value="passive"
                disabled={isProfileReadOnly || !isEditingProfile}
                control={<Radio />}
                label="I'm passively looking for a job"
              />
              <FormControlLabel
                value="active"
                disabled={isProfileReadOnly || !isEditingProfile}
                control={<Radio />}
                label="I'm actively looking for a job"
              />
            </RadioGroup>
          </Grid>
        </Grid>

        <Box
          sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          {/* Show Cancel and Save buttons only for users with existing profiles */}
          {hasExistingProfile && isEditingProfile && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setIsEditingProfile(false);
                  // Reload data from profile/tempProfileData
                  const dataSource = isProfileReadOnly
                    ? profile
                    : tempProfileData.personalInfo || profile;
                  if (dataSource) {
                    setFormData({
                      firstName:
                        dataSource.first_name || dataSource.firstName || "",
                      lastName:
                        dataSource.last_name || dataSource.lastName || "",
                      email: dataSource.email || userEmail || "",
                      isAge18OrAbove: Boolean(
                        dataSource.is_age_18_or_above ||
                        dataSource.isAge18OrAbove ||
                        false,
                      ),
                      country:
                        dataSource.country_code || dataSource.country || "",
                      workEligibleCountries: ["CH"],
                      workEligibleSwiss: dataSource.workEligibleSwiss || [],
                      jobSearchStatus:
                        dataSource.job_search_status ||
                        dataSource.jobSearchStatus ||
                        "passive",
                      function: dataSource.current_industry
                        ? findFunctionIdByName(dataSource.current_industry)
                        : dataSource.function || "",
                      preferredCompanyTypes:
                        dataSource.preferredCompanyTypes || [],
                      preferredWorkEnvironments:
                        dataSource.preferredWorkEnvironments || [],
                      noticePeriod: dataSource.noticePeriod || {
                        value: 7,
                        unit: "days",
                        immediate: false,
                      },
                    });
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={async (e) => {
                  await handleContinue(e);
                }}
              >
                Save & Continue
              </Button>
            </>
          )}
          {!hasExistingProfile && (
            <Button
              type="submit"
              variant="contained"
              size="large"
              endIcon={<NavigateNextIcon />}
              onClick={async (e) => {
                await handleContinue(e);
              }}
            >
              Continue
            </Button>
          )}

          {!isProfileReadOnly && !isEditingProfile && (
            <Button
              type="submit"
              variant="contained"
              size="large"
              endIcon={<NavigateNextIcon />}
            >
              Continue
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default PersonalInfo;
